import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { generateClient } from 'aws-amplify/api';
import { Link, useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiBookmark } from 'react-icons/fi';
import { remove, getUrl } from 'aws-amplify/storage';
import { PageContainer } from '../App';

const client = generateClient();

// Add getS3ImageUrl helper function
const getS3ImageUrl = async (key) => {
  if (!key) return null;
  try {
    const { url } = await getUrl({ path: key });
    return url;
  } catch (err) {
    console.error('Error getting image URL:', err);
    return null;
  }
};

const MetricItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: ${props => props.isBookmarked ? '#1a8917' : '#757575'};

  svg {
    fill: ${props => props.isBookmarked ? 'currentColor' : 'none'};
  }
`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState({ contacts: true, articles: true, subscribers: true });
  const [error, setError] = useState({ contacts: null, articles: null, subscribers: null });
  const [bookmarkedIds, setBookmarkedIds] = useState([]);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const { data: contactsData } = await client.models.Contact.list();
        setContacts(contactsData);
        setError(prev => ({ ...prev, contacts: null }));
      } catch (err) {
        console.error('Error fetching contacts:', err);
        setError(prev => ({ ...prev, contacts: 'Failed to load contacts' }));
      } finally {
        setLoading(prev => ({ ...prev, contacts: false }));
      }
    };

    const fetchArticles = async () => {
      try {
        const { data: articlesData } = await client.models.Article.list();
        
        // Process articles and get signed URLs for images
        const processedArticles = await Promise.all(
          articlesData.map(async article => {
            const fullImageUrl = await getS3ImageUrl(article.bannerImageUrl);
            return {
              ...article,
              bannerImageUrl: fullImageUrl
            };
          })
        );
        
        setArticles(processedArticles);
        setError(prev => ({ ...prev, articles: null }));
      } catch (err) {
        console.error('Error fetching articles:', err);
        setError(prev => ({ ...prev, articles: 'Failed to load articles' }));
      } finally {
        setLoading(prev => ({ ...prev, articles: false }));
      }
    };

    const fetchSubscribers = async () => {
      try {
        const { data: subscribersData } = await client.models.NewsletterSubscriber.list();
        console.log('Fetched subscribers:', subscribersData); // Debug log
        setSubscribers(subscribersData);
        setError(prev => ({ ...prev, subscribers: null }));
      } catch (err) {
        console.error('Error fetching subscribers:', err);
        setError(prev => ({ ...prev, subscribers: 'Failed to load subscribers' }));
      } finally {
        setLoading(prev => ({ ...prev, subscribers: false }));
      }
    };

    // Load bookmarked IDs from localStorage
    const ids = JSON.parse(localStorage.getItem('bookmarkIds') || '[]');
    setBookmarkedIds(ids);

    fetchArticles();
    fetchContacts();
    fetchSubscribers();
  }, []);

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const { data: updatedContact } = await client.models.Contact.update({
        id,
        status: newStatus
      });

      setContacts(contacts.map(contact => 
        contact.id === id ? updatedContact : contact
      ));
    } catch (err) {
      console.error('Error updating contact status:', err);
    }
  };

  const handleSubscriberStatusUpdate = async (id, newStatus) => {
    try {
      const { data: updatedSubscriber } = await client.models.NewsletterSubscriber.update({
        id,
        status: newStatus
      });
      console.log('Updated subscriber:', updatedSubscriber); // Debug log

      setSubscribers(subscribers.map(subscriber => 
        subscriber.id === id ? updatedSubscriber : subscriber
      ));
    } catch (err) {
      console.error('Error updating subscriber status:', err);
      alert('Failed to update subscriber status. Please try again.');
    }
  };

  const handleDeleteArticle = async (articleId) => {
    try {
      // Get the article with its related shortUrls
      const { data: articleWithShortUrls } = await client.models.Article.get(
        { id: articleId },
        { selectionSet: ["id", "shortUrls.*"] }
      );

      // Delete all related shortUrls first
      if (articleWithShortUrls?.shortUrls) {
        await Promise.all(
          articleWithShortUrls.shortUrls.map(shortUrl => 
            client.models.ShortUrl.delete({ id: shortUrl.id })
          )
        );
      }

      // Then delete the article
      await client.models.Article.delete({
        id: articleId
      });
      
      // Update UI
      setArticles(articles.filter(article => article.id !== articleId));
    } catch (error) {
      console.error('Error deleting article:', error);
    }
  };

  const handleEditArticle = (id) => {
    navigate(`/article/${id}/edit`);
  };

  return (
    <PageContainer>
      <AdminContent>
        {/* Articles Section */}
        <Section>
          <PageHeader>
            <PageTitle>Articles</PageTitle>
            <NewArticleButton as={Link} to="/new-story">New Article</NewArticleButton>
          </PageHeader>

          {loading.articles ? (
            <LoadingMessage>Loading articles...</LoadingMessage>
          ) : error.articles ? (
            <ErrorMessage>{error.articles}</ErrorMessage>
          ) : articles.length === 0 ? (
            <NoItems>No articles published yet.</NoItems>
          ) : (
            <ArticleGrid>
              {articles.map((article) => (
                <ArticleCard key={article.id}>
                  {article.bannerImageUrl && (
                    <ArticleImage>
                      <img src={article.bannerImageUrl} alt={article.title} />
                    </ArticleImage>
                  )}
                  <ArticleContent onClick={() => navigate(`/article/${article.id}`)}>
                    <ArticleHeader>
                      <ArticleInfo>
                        <Author>
                          <AuthorImage />
                          <ArticleAuthor>{article.author}</ArticleAuthor>
                        </Author>
                        <ArticleTitle>{article.title}</ArticleTitle>
                      </ArticleInfo>
                    </ArticleHeader>
                    <ArticlePreview>
                      {article.content?.slice(0, 300) || article.description?.slice(0, 300)}
                      {((article.content && article.content.length > 300) || (article.description && article.description.length > 300)) && '...'}
                    </ArticlePreview>
                    <ArticleMeta>
                      <span>
                        {new Date(article.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                      <span>{article.readTime} min read</span>
                      <MetricItem isBookmarked={bookmarkedIds.includes(article.id)}>
                        <FiBookmark />
                      </MetricItem>
                    </ArticleMeta>
                  </ArticleContent>
                  <ArticleActions>
                    <ActionIconButton onClick={() => handleEditArticle(article.id)}>
                      <FiEdit2 />
                    </ActionIconButton>
                    <ActionIconButton onClick={() => handleDeleteArticle(article.id)}>
                      <FiTrash2 />
                    </ActionIconButton>
                  </ArticleActions>
                </ArticleCard>
              ))}
            </ArticleGrid>
          )}
        </Section>

        {/* Subscribers Section */}
        <Section>
          <PageHeader>
            <PageTitle>Newsletter Subscribers</PageTitle>
            <SubscriberCount>{subscribers.length} subscribers</SubscriberCount>
          </PageHeader>

          {loading.subscribers ? (
            <LoadingMessage>Loading subscribers...</LoadingMessage>
          ) : error.subscribers ? (
            <ErrorMessage>{error.subscribers}</ErrorMessage>
          ) : subscribers.length === 0 ? (
            <NoItems>No newsletter subscribers yet.</NoItems>
          ) : (
            <ContactGrid>
              {subscribers.map((subscriber) => (
                <ContactCard key={subscriber.id}>
                  <ContactHeader>
                    <ContactInfo>
                      <ContactEmail>{subscriber.email}</ContactEmail>
                      <ContactDate>
                        Subscribed on {new Date(subscriber.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </ContactDate>
                    </ContactInfo>
                    <StatusBadge status={subscriber.status}>
                      {subscriber.status}
                    </StatusBadge>
                  </ContactHeader>
                  <ContactMeta>
                    <ContactActions>
                      {subscriber.status !== 'unsubscribed' && (
                        <ActionButton
                          onClick={() => handleSubscriberStatusUpdate(subscriber.id, 'unsubscribed')}
                        >
                          Unsubscribe
                        </ActionButton>
                      )}
                      {subscriber.status === 'unsubscribed' && (
                        <ActionButton
                          onClick={() => handleSubscriberStatusUpdate(subscriber.id, 'active')}
                        >
                          Reactivate
                        </ActionButton>
                      )}
                    </ContactActions>
                  </ContactMeta>
                </ContactCard>
              ))}
            </ContactGrid>
          )}
        </Section>

        {/* Contacts Section */}
        <Section>
          <PageHeader>
            <PageTitle>Contact Submissions</PageTitle>
          </PageHeader>

          {loading.contacts ? (
            <LoadingMessage>Loading contacts...</LoadingMessage>
          ) : error.contacts ? (
            <ErrorMessage>{error.contacts}</ErrorMessage>
          ) : contacts.length === 0 ? (
            <NoItems>No contact submissions yet.</NoItems>
          ) : (
            <ContactGrid>
              {contacts.map((contact) => (
                <ContactCard key={contact.id}>
                  <ContactHeader>
                    <ContactInfo>
                      <ContactName>{contact.name}</ContactName>
                      <ContactEmail>{contact.email}</ContactEmail>
                    </ContactInfo>
                    <StatusBadge status={contact.status}>
                      {contact.status}
                    </StatusBadge>
                  </ContactHeader>
                  <ContactMessage>{contact.message}</ContactMessage>
                  <ContactMeta>
                    <ContactDate>
                      {new Date(contact.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </ContactDate>
                    <ContactActions>
                      {contact.status !== 'read' && (
                        <ActionButton
                          onClick={() => handleStatusUpdate(contact.id, 'read')}
                        >
                          Mark as Read
                        </ActionButton>
                      )}
                      {contact.status !== 'responded' && (
                        <ActionButton
                          onClick={() => handleStatusUpdate(contact.id, 'responded')}
                        >
                          Mark as Responded
                        </ActionButton>
                      )}
                    </ContactActions>
                  </ContactMeta>
                </ContactCard>
              ))}
            </ContactGrid>
          )}
        </Section>
      </AdminContent>
    </PageContainer>
  );
};

const AdminContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Section = styled.section`
  margin-bottom: 3rem;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  font-family: 'Source Serif 4', Georgia, serif;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const NewArticleButton = styled.button`
  background: #1a8917;
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 99px;
  font-size: 1rem;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    background: #156912;
  }
`;

const ArticleGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const ArticleCard = styled.div`
  background: white;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 0px 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  display: flex;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ArticleImage = styled.div`
  width: 280px;
  flex-shrink: 0;
  background: #f8f9fa;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 200px;
    position: relative;
  }
`;

const ArticleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ArticleInfo = styled.div`
  flex: 1;
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 0.5rem;
`;

const AuthorImage = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: url('/author.jpeg');
  background-size: cover;
  background-position: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const ArticleTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
  color: #292929;
  transition: color 0.2s ease;
`;

const ArticleAuthor = styled.span`
  color: #757575;
  font-size: 0.9rem;
`;

const ArticleActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: 1rem;
  flex-shrink: 0;
`;

const ActionIconButton = styled.button`
  background: none;
  border: none;
  color: #757575;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: #f5f5f5;
    color: #292929;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ArticleMeta = styled.div`
  display: flex;
  gap: 1rem;
  color: #757575;
  font-size: 0.9rem;
`;

const ContactGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const ContactCard = styled.div`
  padding: 1.5rem;
  border-radius: 4px;
  background: white;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 0px 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ContactHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const ContactName = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #292929;
`;

const ContactEmail = styled.span`
  color: #757575;
  font-size: 0.9rem;
`;

const StatusBadge = styled.span`
  padding: 0.3rem 0.8rem;
  border-radius: 99px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: capitalize;
  
  ${props => {
    switch (props.status) {
      case 'new':
        return 'background: #e3f2fd; color: #1976d2;';
      case 'read':
        return 'background: #f3e5f5; color: #7b1fa2;';
      case 'responded':
        return 'background: #e8f5e9; color: #388e3c;';
      default:
        return 'background: #f5f5f5; color: #757575;';
    }
  }}
`;

const ContactMessage = styled.p`
  margin: 0 0 1rem;
  line-height: 1.5;
  color: #292929;
`;

const ContactMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e6e6e6;
  padding-top: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.8rem;
  }
`;

const ContactDate = styled.span`
  color: #757575;
  font-size: 0.9rem;
`;

const ContactActions = styled.div`
  display: flex;
  gap: 0.8rem;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid #1a8917;
  color: #1a8917;
  padding: 0.5rem 1rem;
  border-radius: 99px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #1a8917;
    color: white;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.8rem;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: #757575;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    padding: 3rem 0;
    font-size: 1.1rem;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: #e53935;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    padding: 3rem 0;
    font-size: 1.1rem;
  }
`;

const NoItems = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: #757575;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    padding: 3rem 0;
    font-size: 1.1rem;
  }
`;

const ArticleContent = styled.div`
  flex: 1;
  cursor: pointer;
  padding: 1.5rem;

  &:hover {
    ${ArticleTitle} {
      color: #1a8917;
    }
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ArticlePreview = styled.p`
  color: #757575;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0 0 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Add styled component for subscriber count
const SubscriberCount = styled.div`
  background: #e8f5e9;
  color: #1a8917;
  padding: 0.5rem 1rem;
  border-radius: 99px;
  font-size: 0.9rem;
  font-weight: 500;
`;

export default AdminPage;